package com.htmlagent.site.components.sections

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.htmlagent.site.i18n.BriefStrings
import com.htmlagent.site.i18n.LocalStrings
import com.htmlagent.site.state.brief.Brief
import com.htmlagent.site.state.brief.BriefPresenter
import com.htmlagent.site.state.brief.BriefStep
import com.htmlagent.site.state.brief.Gesture
import com.htmlagent.site.state.brief.ImagerySource
import com.htmlagent.site.state.brief.ImageryTreatment
import com.htmlagent.site.state.brief.PageKind
import com.htmlagent.site.state.brief.Palette
import com.htmlagent.site.state.brief.Register
import com.htmlagent.site.state.brief.sectionsOf
import com.htmlagent.site.state.brief.toPrompt
import com.htmlagent.site.theme.BriefBadgeStyle
import com.htmlagent.site.theme.BriefBodyStyle
import com.htmlagent.site.theme.BriefBudgetStyle
import com.htmlagent.site.theme.BriefCardLineStyle
import com.htmlagent.site.theme.BriefCardHeadStyle
import com.htmlagent.site.theme.BriefCardNameStyle
import com.htmlagent.site.theme.BriefCardStyle
import com.htmlagent.site.theme.BriefChipStyle
import com.htmlagent.site.theme.BriefCloseStyle
import com.htmlagent.site.theme.BriefColourPickerStyle
import com.htmlagent.site.theme.BriefColourWellStyle
import com.htmlagent.site.theme.BriefDialogStyle
import com.htmlagent.site.theme.BriefDisclosureStyle
import com.htmlagent.site.theme.BriefEyebrowStyle
import com.htmlagent.site.theme.BriefFieldStyle
import com.htmlagent.site.theme.BriefFootStyle
import com.htmlagent.site.theme.BriefGridStyle
import com.htmlagent.site.theme.BriefHeadStyle
import com.htmlagent.site.theme.BriefHeadTextStyle
import com.htmlagent.site.theme.BriefHeadingStyle
import com.htmlagent.site.theme.BriefLabelStyle
import com.htmlagent.site.theme.BriefLedeStyle
import com.htmlagent.site.theme.BriefLineOpenStyle
import com.htmlagent.site.theme.BriefLineStyle
import com.htmlagent.site.theme.BriefNoteStyle
import com.htmlagent.site.theme.BriefPaneStyle
import com.htmlagent.site.theme.BriefRampStyle
import com.htmlagent.site.theme.BriefScrimStyle
import com.htmlagent.site.theme.BriefStackStyle
import com.htmlagent.site.theme.BriefStepNumberStyle
import com.htmlagent.site.theme.BriefStepsStyle
import com.htmlagent.site.theme.BriefStepStyle
import com.htmlagent.site.theme.BriefSummaryStyle
import com.htmlagent.site.theme.BriefSwatchStyle
import com.htmlagent.site.theme.BriefTagStyle
import com.htmlagent.site.theme.BriefTypeRowStyle
import com.htmlagent.site.theme.BriefWrapStyle
import com.htmlagent.site.theme.Tokens
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.rows
import org.jetbrains.compose.web.css.StyleScope
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea
import org.w3c.dom.events.KeyboardEvent

/**
 * The commission assistant: four questions between "I want a landing page" and a page.
 *
 * It exists because the agent's own skill says the weakest input it gets is a brief in the
 * abstract — "something modern and clean" describes a thousand pages — and because the four
 * decisions it asks about are the ones the skills grade everything else by. What it does not do
 * is block: every question can be skipped, and a skipped one contributes nothing rather than
 * contributing a default.
 *
 * The palette is the one place it decides instead of asking. Nobody can pick twelve colours that
 * hold together, so it takes one and derives the rest.
 */
@Composable
fun BriefDialog(presenter: BriefPresenter, onGenerate: (String) -> Unit) {
    val t = LocalStrings.current.brief
    val visible by presenter.visible.collectAsState()
    val step by presenter.step.collectAsState()
    val draft by presenter.draft.collectAsState()

    // What is sent to the agent, and the notes that only make sense to somebody who reads it,
    // start folded away. The panel is honest and worth keeping; opening on a hex code, a hue in
    // degrees and a contrast ratio is what makes four plain questions look like a design tool.
    var expert by remember { mutableStateOf(false) }

    // Wrapped in an `if` rather than guarded by an early `return`. With the return, the
    // composable produced nothing at all and went on producing nothing after the state
    // flipped: no error anywhere, just a button that looked broken.
    if (visible) {
        // Escape closes it. Registered on the document and not on the dialog: the focus may be
        // anywhere inside, and a listener per control is a listener somebody forgets to add.
        DisposableEffect(Unit) {
            val onKey: (org.w3c.dom.events.Event) -> Unit = { event ->
                if ((event as? KeyboardEvent)?.key == "Escape") presenter.hide()
            }
            document.addEventListener("keydown", onKey)
            onDispose { document.removeEventListener("keydown", onKey) }
        }

        Div(BriefScrimStyle.toModifier().onClick { presenter.hide() }.toAttrs()) {
            Div(
                BriefDialogStyle.toModifier()
                    .styleModifier { property("animation", RISE) }
                    // The scrim closes; the dialog is not the scrim.
                    .onClick { it.stopPropagation() }
                    .toAttrs {
                        attr("role", "dialog")
                        attr("aria-modal", "true")
                        attr("aria-label", t.title)
                        // The dialog narrows to the questions when the panel is folded away,
                        // so the pane is not left floating in a box sized for two columns.
                        attr("data-expert", expert.toString())
                    },
            ) {
                Head(t, step, draft, presenter)
                Div(BriefBodyStyle.toModifier().toAttrs()) {
                    Div(
                        BriefPaneStyle.toModifier()
                            .toAttrs { attr("data-expert", expert.toString()) },
                    ) {
                        when (step) {
                            BriefStep.Commission -> CommissionStep(t, draft, presenter)
                            BriefStep.Register -> RegisterStep(t, draft, presenter)
                            BriefStep.Colour -> ColourStep(t, draft, presenter, expert)
                            BriefStep.Form -> FormStep(t, draft, presenter)
                        }
                        Disclosure(t, expert) { expert = !expert }
                    }
                    if (expert) Summary(t, draft)
                }
                Foot(t, step, presenter) { onGenerate(draft.toPrompt(t)) }
            }
        }
    }
}

@Composable
private fun Head(t: BriefStrings, step: BriefStep, draft: Brief, presenter: BriefPresenter) {
    Div(BriefHeadStyle.toModifier().toAttrs()) {
        Div(BriefBadgeStyle.toModifier().toAttrs()) { SparkIcon() }
        Div(BriefHeadTextStyle.toModifier().toAttrs()) {
            Div(Modifier.styleModifier { property("font-weight", "600") }.toAttrs()) { Text(t.title) }
            Div(BriefLedeStyle.toModifier().toAttrs()) { Text(t.subtitle) }
        }
        Div(BriefStepsStyle.toModifier().toAttrs()) {
            BriefStep.entries.forEach { entry ->
                Button(
                    onClick = { presenter.goTo(entry) },
                    modifier = BriefStepStyle.toModifier().attrsModifier {
                        attr("data-on", (entry == step).toString())
                        attr("aria-current", (entry == step).toString())
                    },
                    variant = null,
                ) {
                    Span(
                        BriefStepNumberStyle.toModifier().toAttrs {
                            attr("data-on", (entry == step).toString())
                        },
                    ) { Text("${entry.number}") }
                    Text(t.stepName(entry.name.lowercase()))
                    if (!draft.decided(entry)) Span(BriefTagStyle.toModifier().toAttrs()) { Text("—") }
                }
            }
        }

        Button(
            onClick = { presenter.hide() },
            modifier = BriefCloseStyle.toModifier().attrsModifier { attr("aria-label", t.close) },
            variant = null,
        ) { CloseIcon() }
    }
}

/** The way out. Escape and the backdrop close it too, but neither is visible. */
@Composable
private fun CloseIcon() {
    Div(
        Modifier.styleModifier {
            property("width", "15px")
            property("height", "15px")
            property("background", "currentColor")
            property("mask", "$CROSS no-repeat center / contain")
            property("-webkit-mask", "$CROSS no-repeat center / contain")
        }.toAttrs(),
    ) {}
}

@Composable
private fun CommissionStep(t: BriefStrings, draft: Brief, presenter: BriefPresenter) {
    Question(t.commissionTitle, t.commissionLede)

    Group(t.kindLabel) {
        Div(BriefGridStyle.toModifier().toAttrs()) {
            PageKind.entries.forEach { kind ->
                Choice(
                    name = t.kindName(kind.id),
                    line = t.kindLine(kind.id),
                    selected = draft.kind == kind,
                    tag = if (kind.isDefault) t.kindDefault else null,
                ) { presenter.edit { withKind(kind) } }
            }
        }
    }

    Group(t.whatLabel) {
        TextArea(
            value = draft.what,
            attrs = BriefFieldStyle.toModifier().toAttrs {
                rows(WHAT_ROWS)
                attr("placeholder", t.whatPlaceholder)
                attr("aria-label", t.whatLabel)
                onInput { event -> presenter.edit { decide(BriefStep.Commission).copy(what = event.value) } }
            },
        )
    }

    Group(t.actionLabel) {
        Div(BriefWrapStyle.toModifier().toAttrs()) {
            draft.kind.actions.forEach { action ->
                Chip(t.actionName(action.id), draft.action == action) {
                    presenter.edit { decide(BriefStep.Commission).copy(action = action) }
                }
            }
        }
        Note(t.kindNote(draft.kind.id))
    }

    Group("${t.referencesLabel} — ${t.referencesHint}") {
        Input(
            type = org.jetbrains.compose.web.attributes.InputType.Text,
            attrs = BriefFieldStyle.toModifier().toAttrs {
                value(draft.references)
                attr("placeholder", t.referencesPlaceholder)
                attr("aria-label", t.referencesLabel)
                onInput { event -> presenter.edit { copy(references = event.value) } }
            },
        )
    }
}

@Composable
private fun RegisterStep(t: BriefStrings, draft: Brief, presenter: BriefPresenter) {
    Question(t.registerTitle, t.registerLede)

    Div(BriefGridStyle.toModifier().toAttrs()) {
        Register.entries.forEach { register ->
            Choice(
                name = t.registerName(register.id),
                line = t.registerLine(register.id),
                selected = draft.register == register,
            ) { presenter.edit { withRegister(register) } }
        }
    }

    Group(t.budgetTitle(t.registerName(draft.register.id))) {
        val budget = draft.register.budget
        Div(BriefBudgetStyle.toModifier().toAttrs()) {
            BudgetCell(t.budgetAccents, "${budget.accents}")
            BudgetCell(t.budgetGestures, "${budget.gestures}")
            BudgetCell(t.budgetMotion, t.motionName(budget.motion.id))
            BudgetCell(t.budgetRadii, "${budget.radii}")
            BudgetCell(t.budgetDensity, t.densityName(budget.density.id))
        }
        Note(t.ungraded)
    }
}

@Composable
private fun ColourStep(t: BriefStrings, draft: Brief, presenter: BriefPresenter, expert: Boolean) {
    Question(t.colourTitle, t.colourLede)

    val seed = draft.seed
    val ramp = seed?.let { Palette.accentRamp(it) }.orEmpty()
    val solid = ramp.getOrNull(SOLID)?.hex ?: draft.accent

    Div(Modifier.styleModifier { property("display", "flex"); property("gap", "16px") }.toAttrs()) {
        Div(
            BriefColourWellStyle.toModifier()
                .styleModifier { property("background", solid) }
                .toAttrs(),
        ) {
            Input(
                type = org.jetbrains.compose.web.attributes.InputType.Color,
                attrs = BriefColourPickerStyle.toModifier().toAttrs {
                    value(Palette.normalize(draft.accent) ?: Brief.DEFAULT_ACCENT)
                    attr("aria-label", t.pickerLabel)
                    attr("title", t.pickerLabel)
                    onInput { event -> presenter.edit { decide(BriefStep.Colour).copy(accent = event.value) } }
                },
            )
        }
        Div(BriefStackStyle.toModifier().toAttrs()) {
            Span(BriefLabelStyle.toModifier().toAttrs()) { Text(t.accentLabel) }
            Input(
                type = org.jetbrains.compose.web.attributes.InputType.Text,
                attrs = BriefFieldStyle.toModifier()
                    .then(Modifier.styleModifier { property("width", "128px") })
                    .toAttrs {
                        value(draft.accent)
                        attr("aria-label", t.accentLabel)
                        onInput { event -> presenter.edit { decide(BriefStep.Colour).copy(accent = event.value) } }
                    },
            )
            // A hue in degrees is a note for whoever is reading the brief, not an answer to
            // "what colour is your shop": it travels with the rest of the panel.
            if (expert) {
                Span(BriefLineOpenStyle.toModifier().toAttrs()) {
                    val hue = Palette.hue(solid)
                    Text(
                        when {
                            hue == null -> t.hueNote("—")
                            Palette.isSuspectHue(solid) -> t.hueSuspect("${Palette.degrees(hue)}°")
                            else -> t.hueNote("${Palette.degrees(hue)}°")
                        },
                    )
                }
            }
        }
    }

    Group(t.otherColours) {
        Div(BriefWrapStyle.toModifier().toAttrs()) {
            SWATCHES.forEach { hex ->
                Button(
                    onClick = { presenter.edit { decide(BriefStep.Colour).copy(accent = hex) } },
                    modifier = BriefSwatchStyle.toModifier()
                        .styleModifier { property("background", hex) }
                        .attrsModifier {
                            attr("data-on", hex.equals(draft.accent, ignoreCase = true).toString())
                            attr("aria-label", t.swatchName(hex))
                            attr("title", t.swatchName(hex))
                        },
                    variant = null,
                ) {}
            }
        }
    }

    if (seed != null) {
        // The two ramps stay: they are the colour, and anybody can see whether they like it.
        // The measured ratio and the note about grey are craft notes and go with the panel.
        Group(t.accentScale) { Ramp(ramp.map { it.hex }, RAMP_HEIGHT) }
        Group(t.neutralScale) { Ramp(Palette.neutralRamp(seed).map { it.hex }, NEUTRAL_HEIGHT) }
        if (expert) {
            val ink = ramp[INK].hex
            val onSolid = Palette.onSolid(solid, ink)
            Span(BriefLineOpenStyle.toModifier().toAttrs()) {
                val measured = Palette.contrast(solid, onSolid)
                Text(t.onSolidLabel("${ratio(measured)} — ${pass(t, measured)}"))
            }
            Note(t.greyNote)
        }
    }
}

/**
 * The one control that unfolds the brief being written and the notes that go with it.
 *
 * It sits at the foot of the questions and not in the header or the footer: there it would read
 * as a fifth way out of a dialog that already has three, and it is not a way out of anything.
 */
@Composable
private fun Disclosure(t: BriefStrings, expert: Boolean, onToggle: () -> Unit) {
    Button(
        onClick = { onToggle() },
        modifier = BriefDisclosureStyle.toModifier().attrsModifier {
            attr("aria-expanded", expert.toString())
        },
        variant = null,
    ) {
        Text(if (expert) t.panelHide else t.panelShow)
    }
}

@Composable
private fun FormStep(t: BriefStrings, draft: Brief, presenter: BriefPresenter) {
    Question(t.formTitle, t.formLede(t.registerName(draft.register.id)))

    Group(t.typeLabel) {
        draft.register.pairings.forEachIndexed { index, pairing ->
            Button(
                onClick = { presenter.edit { decide(BriefStep.Form).copy(pairing = index) } },
                modifier = BriefTypeRowStyle.toModifier()
                    .attrsModifier { attr("data-on", (draft.pairing == index).toString()) },
                variant = null,
            ) {
                Div(Modifier.styleModifier { property("flex", "1"); property("min-width", "0") }.toAttrs()) {
                    Div(
                        Modifier.styleModifier {
                            property("font-family", pairing.displayStack)
                            property("font-size", "26px")
                            property("line-height", "1.05")
                        }.toAttrs(),
                    ) { Text(t.sampleDisplay) }
                    Div(
                        Modifier.styleModifier {
                            property("font-family", pairing.bodyStack)
                            property("font-size", "12px")
                            property("color", Tokens.muted.value().toString())
                            property("margin-top", "4px")
                        }.toAttrs(),
                    ) { Text(t.sampleBody) }
                }
                Span(BriefCardLineStyle.toModifier().toAttrs()) { Text("${pairing.display} · ${pairing.body}") }
            }
        }
    }

    Group(t.imageryLabel) {
        Div(BriefGridStyle.toModifier().toAttrs()) {
            ImagerySource.entries.forEach { source ->
                Choice(
                    name = t.imageryName(source.id),
                    line = t.imageryLine(source.id),
                    selected = draft.imagery == source,
                ) { presenter.edit { decide(BriefStep.Form).copy(imagery = source) } }
            }
        }
        if (draft.imagery.hasTreatment) {
            Div(BriefWrapStyle.toModifier().toAttrs()) {
                ImageryTreatment.entries.forEach { treatment ->
                    Chip(t.treatmentName(treatment.id), draft.treatment == treatment) {
                        presenter.edit { decide(BriefStep.Form).copy(treatment = treatment) }
                    }
                }
            }
        }
    }

    Group("${t.gestureLabel} — ${t.gestureChosen("${draft.gestures.size}/${draft.register.budget.gestures}")}") {
        val blocked = draft.blockedGestures()
        Div(BriefWrapStyle.toModifier().toAttrs()) {
            Gesture.entries.forEach { gesture ->
                Button(
                    onClick = { presenter.edit { toggleGesture(gesture) } },
                    modifier = BriefChipStyle.toModifier().attrsModifier {
                        attr("data-on", (gesture in draft.gestures).toString())
                        attr("data-blocked", (gesture in blocked).toString())
                    },
                    variant = null,
                ) { Text(t.gestureName(gesture.id)) }
            }
        }
        Note(if (draft.register.budget.gestures == 1) t.gestureNoteOne else t.gestureNoteTwo)
    }
}

/**
 * The brief as it is being written, which is literally the text that will be sent.
 *
 * Showing the real thing rather than a friendly rendering of it is the point: somebody who can
 * read what is about to leave can correct it, and somebody who cannot has to trust us.
 */
@Composable
private fun Summary(t: BriefStrings, draft: Brief) {
    Div(
        BriefSummaryStyle.toModifier().toAttrs {
            attr("role", "region")
            attr("aria-label", t.panelTitle)
        },
    ) {
        Span(BriefEyebrowStyle.toModifier().toAttrs()) { Text(t.panelTitle) }
        Span(BriefLineStyle.toModifier().toAttrs()) { Text(draft.toPrompt(t)) }
        BriefStep.entries.filterNot { draft.decided(it) }.forEach { open ->
            Span(BriefLineOpenStyle.toModifier().toAttrs()) {
                Text("${t.stepName(open.name.lowercase())}: ${t.undecided}")
            }
        }
        if (draft.decided(BriefStep.Commission)) {
            Span(BriefLineOpenStyle.toModifier().toAttrs()) {
                Text(sectionsOf(draft.kind, t).size.toString() + " · " + t.lblStructure.lowercase())
            }
        }
    }
}

@Composable
private fun Foot(t: BriefStrings, step: BriefStep, presenter: BriefPresenter, onGenerate: () -> Unit) {
    Div(BriefFootStyle.toModifier().toAttrs()) {
        Quiet(t.writeMyself) { presenter.hide() }
        Div(Modifier.styleModifier { property("flex", "1") }.toAttrs()) {}
        Quiet(t.skipStep) { presenter.skip() }
        Ghost(t.back) { presenter.back() }
        Primary(if (step == BriefStep.Form) t.generate else t.next) {
            if (step == BriefStep.Form) {
                onGenerate()
                presenter.reset()
            } else {
                presenter.next()
            }
        }
    }
}

// ── small pieces ────────────────────────────────────────────────────────────

@Composable
private fun Question(title: String, lede: String) {
    Div {
        H2(BriefHeadingStyle.toModifier().toAttrs()) { Text(title) }
        P(BriefLedeStyle.toModifier().toAttrs()) { Text(lede) }
    }
}

@Composable
private fun Group(label: String, content: @Composable () -> Unit) {
    Div(BriefStackStyle.toModifier().toAttrs()) {
        Span(BriefLabelStyle.toModifier().toAttrs()) { Text(label) }
        content()
    }
}

@Composable
private fun Choice(name: String, line: String, selected: Boolean, tag: String? = null, onPick: () -> Unit) {
    Button(
        onClick = { onPick() },
        modifier = BriefCardStyle.toModifier().attrsModifier {
            attr("data-on", selected.toString())
            attr("aria-pressed", selected.toString())
        },
        variant = null,
    ) {
        Div(BriefCardHeadStyle.toModifier().toAttrs()) {
            Span(BriefCardNameStyle.toModifier().toAttrs()) { Text(name) }
            if (tag != null) Span(BriefTagStyle.toModifier().toAttrs()) { Text(tag) }
        }
        Span(BriefCardLineStyle.toModifier().toAttrs()) { Text(line) }
    }
}

@Composable
private fun Chip(label: String, selected: Boolean, onPick: () -> Unit) {
    Button(
        onClick = { onPick() },
        modifier = BriefChipStyle.toModifier().attrsModifier {
            attr("data-on", selected.toString())
            attr("aria-pressed", selected.toString())
        },
        variant = null,
    ) { Text(label) }
}

@Composable
private fun BudgetCell(key: String, value: String) {
    Div(BriefStackStyle.toModifier().toAttrs()) {
        Span(BriefEyebrowStyle.toModifier().toAttrs()) { Text(key) }
        Span(BriefCardNameStyle.toModifier().toAttrs()) { Text(value) }
    }
}

@Composable
private fun Ramp(colours: List<String>, height: Int) {
    Div(BriefRampStyle.toModifier().toAttrs()) {
        colours.forEach { hex ->
            Div(
                Modifier.styleModifier {
                    property("flex", "1")
                    property("height", "${height}px")
                    property("background", hex)
                }.toAttrs(),
            ) {}
        }
    }
}

@Composable
private fun Note(text: String) {
    Div(BriefNoteStyle.toModifier().toAttrs()) { Text(text) }
}

@Composable
private fun Primary(label: String, onClick: () -> Unit) = FootButton(label, onClick) {
    property("background", Tokens.accent.value().toString())
    property("color", Tokens.onAccent.value().toString())
}

@Composable
private fun Ghost(label: String, onClick: () -> Unit) = FootButton(label, onClick) {
    property("background", "transparent")
    property("color", Tokens.muted.value().toString())
    property("border-color", Tokens.line.value().toString())
}

@Composable
private fun Quiet(label: String, onClick: () -> Unit) = FootButton(label, onClick) {
    property("background", "transparent")
    property("color", Tokens.muted.value().toString())
}

@Composable
private fun FootButton(label: String, onPress: () -> Unit, paint: StyleScope.() -> Unit) {
    Button(
        onClick = { onPress() },
        modifier = Modifier.styleModifier {
            property("height", "${Tokens.controlLg.value()}")
            property("padding", "0 20px")
            property("border", "1px solid transparent")
            property("border-radius", "${Tokens.radiusMd.value()}")
            property("font-family", "inherit")
            property("font-size", "${Tokens.textSm.value()}")
            property("font-weight", "600")
            property("cursor", "pointer")
            paint()
        },
        variant = null,
    ) { Text(label) }
}

@Composable
private fun SparkIcon() {
    Div(
        Modifier.styleModifier {
            property("width", "17px")
            property("height", "17px")
            property("background", "currentColor")
            property("mask", "$SPARK no-repeat center / contain")
            property("-webkit-mask", "$SPARK no-repeat center / contain")
        }.toAttrs(),
    ) {}
}

private const val SPARK =
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' " +
        "stroke='%23000' stroke-width='1.7' stroke-linejoin='round'%3E%3Cpath d='M12 3.2l1.9 4.9 4.9 1.9" +
        "-4.9 1.9-1.9 4.9-1.9-4.9L5.2 10l4.9-1.9z'/%3E%3C/svg%3E\")"

/** Three lines: enough for a commission, short enough that it does not read as an essay. */
private const val WHAT_ROWS = 3

private const val CROSS =
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' " +
        "stroke='%23000' stroke-width='1.9' stroke-linecap='round'%3E%3Cpath d='M6 6l12 12'/%3E" +
        "%3Cpath d='M18 6L6 18'/%3E%3C/svg%3E\")"

private const val RISE = "ha-brief-rise 260ms cubic-bezier(0.22, 1, 0.36, 1) backwards"

private val SWATCHES = listOf("#8A4B2A", "#B85235", "#5E6B3A", "#1E7A82", "#4F5BD5", "#7A3B6B")

private const val SOLID = 8
private const val INK = 11
private const val RAMP_HEIGHT = 40
private const val NEUTRAL_HEIGHT = 26

private const val RATIO_DECIMALS = 2

private fun ratio(value: Double): String = "${value.asDynamic().toFixed(RATIO_DECIMALS) as String}:1"

private fun pass(t: BriefStrings, value: Double): String = when {
    value >= Palette.AA_BODY -> t.contrastPass("body")
    value >= Palette.AA_NON_TEXT -> t.contrastPass("display")
    else -> t.contrastPass("fail")
}
